import React, { useState, useEffect, useMemo, useRef, lazy } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Line, LabelList, Legend
} from "../utils/lazyRecharts";

import { useDashboard } from "./dashboardStore";
import downloadLogo from "/download.svg"

// Reusable Tailwind Card component
const Card = ({ children, className = "" }) => (
  <div className={`bg-white shadow rounded p-4 ${className}`}>{children}</div>
);

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
const MAX_CUT_OFF = 40000; //40k to be branded nice contributor
const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split("-").map(Number);
  const date = new Date(year, month - 1); // JS months are 0-indexed
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const CustomerSupplierMap = () => {
  // State for selected month
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  // Handlers to navigate months
  const handleNextMonth = () => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const newMonth = month === 12 ? 1 : month + 1;
    const newYear = month === 12 ? year + 1 : year;
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  const handlePrevMonth = () => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const newMonth = month === 1 ? 12 : month - 1;
    const newYear = month === 1 ? year - 1 : year;
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  // Customer state
  const [customerData, setCustomerData] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
// Subscribe to dashboard store slice
const customersDat = useDashboard(state => state.data?.customers);

// -------------------------------------
// Process customers data
// -------------------------------------
useEffect(() => {
	// Wait until store has data
	if (!customersDat) return;

	const processCustomers = () => {
		try {
			// Normalize each customer row
			const normalized = customersDat.map((c) => ({
				id: Number(c.Id),
				name: c.Name,
				revenue: Number(c.Revenue || 0),
				growth: c.Growth,
				region: c.Region,
				month: c.month,
				typeImport: c.Type
			}));

			// Sort by revenue descending
			normalized.sort((a, b) => b.revenue - a.revenue);

			// Update state
			setCustomerData(normalized);
		} catch (err) {
			console.error("Error processing customers:", err);
		} finally {
			setLoadingCustomers(false);
		}
	};

	processCustomers();
}, [customersDat]); // Reactive: runs whenever store updates

  
    // Compute customers for selected month with previous month data
  const customersForMonth = useMemo(() => {
    const prevMonth = (ym) => {
      const [y, m] = ym.split("-").map(Number);
      const newMonth = m === 1 ? 12 : m - 1;
      const newYear = m === 1 ? y - 1 : y;
      return `${newYear}-${String(newMonth).padStart(2, "0")}`;
    };
       return customerData
      .filter((c) => c.month?.slice(0, 7) === selectedMonth)
      .map((c) => {
        const prev = customerData.find(
          (p) => p.name === c.name && p.month?.slice(0, 7) === prevMonth(c.month.slice(0, 7))
        );
        const growth =
          prev && prev.revenue
            ? Math.round(((c.revenue - prev.revenue) / prev.revenue) * 100)
            : null;
        return { ...c, previousMonth: prev?.revenue || 0, growth };
      });
  }, [customerData, selectedMonth]);

  // Top customer revenue %
  const topCustomerPercent = useMemo(() => {
    if (!customersForMonth || customersForMonth.length === 0) return 0;
    const totalRevenue = customersForMonth.reduce((sum, c) => sum + c.revenue, 0);
    return totalRevenue ? (customersForMonth[0].revenue / totalRevenue) * 100 : 0;
  }, [customersForMonth]);


  // Filters
  const [customerFilter, setCustomerFilter] = useState("All");

  const filteredCustomers =
    customerFilter === "All"
      ? customersForMonth
      : customersForMonth.filter((c) => c.region === customerFilter);


    // --------- suppliers information ----------
// Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const didInitialFetchSuppliers = useRef(false);

// Subscribe to dashboard store slice
const suppliersDat = useDashboard(state => state.data?.suppliers);

// -------------------------------------
// Process suppliers data
// -------------------------------------
useEffect(() => {
	// Wait until store has data
	if (!suppliersDat) return;

	const processSuppliers = () => {
		try {
			// Normalize each supplier row
			const normalized = suppliersDat.map((s) => ({
				id: Number(s.Id),
				name: s.Name,
				purchase: Number(s.Purchase || 0),
				region: s.Region,
				month: s.month,
				typeImport: s.Type,
			}));

			// Sort by purchase descending
			normalized.sort((a, b) => b.purchase - a.purchase);

			setSuppliers(normalized);
		} catch (err) {
			console.error("Error processing suppliers:", err);
		}
	};

	processSuppliers();
}, [suppliersDat]); // Reactive: runs whenever store updates

    // Compute suppliers for selected month with previous month data
    const suppliersForMonth = useMemo(() => {
      const prevMonth = (ym) => {
        const [y, m] = ym.split("-").map(Number);
        const newMonth = m === 1 ? 12 : m - 1;
        const newYear = m === 1 ? y - 1 : y;
        return `${newYear}-${String(newMonth).padStart(2, "0")}`;
      };

      return suppliers
        .filter((s) => s.month?.slice(0, 7) === selectedMonth)
        .map((s) => {
          const prev = suppliers.find(
            (p) => p.name === s.name && p.month?.slice(0, 7) === prevMonth(s.month.slice(0, 7))
          );
          return { ...s, previousMonthPurchase: prev?.purchase || 0 };
        });
    }, [suppliers, selectedMonth]);
   

  // Top supplier cost/share %
  const topSupplierPercent = useMemo(() => {
    if (!suppliersForMonth || suppliersForMonth.length === 0) return 0;
    const totalCost = suppliersForMonth.reduce((sum, c) => sum + c.purchase, 0);
    
    return totalCost ? (suppliersForMonth[0].purchase / totalCost) * 100 : 0;
  }, [suppliersForMonth]);

  const [supplierFilter, setSupplierFilter] = useState("All");
    const filteredSuppliers = (() => {
      // Filter by region
      const filtered = supplierFilter === "All"
        ? suppliersForMonth
        : suppliersForMonth.filter((s) => s.region === supplierFilter);
    
      // Compute total purchase for the filtered suppliers
      const totalCost = filtered.reduce((sum, s) => sum + s.purchase, 0);
    
      // Add custom fields per supplier
      return filtered.map((s) => {
        const deltaCost = s.purchase - s.previousMonthPurchase;  // per supplier
        const growthPercent = s.previousMonthPurchase
          ? deltaCost / s.previousMonthPurchase
          : 0;
    
        return {
          ...s,
          share: totalCost ? Number(((s.purchase / totalCost) * 100).toFixed(1)) : 0,
          growth: Number((growthPercent * 100).toFixed(2)),
        };
      });
    })();

    

  // CSV export function
  const exportCSV = (data, filename) => {
    const csv = [
      Object.keys(data[0]).join(","),
      ...data.map((row) => Object.values(row).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.download = filename;
    link.href = window.URL.createObjectURL(blob);
    link.click();
  };

  // Compute region density & color intensity for heatmap
  const dynamicRegionDensity = useMemo(() => {
    const map = {};
    filteredSuppliers.forEach((m) => {
      if (!map[m.region]) map[m.region] = 0;
      map[m.region] += m.share + 15; 
          // sum growth if multiple markets per region
          // normalize near zero values so they can be seen lol
    });
    return map;
  }, [filteredSuppliers]);


  const maxAbsDensity = Math.max(
    ...Object.values(dynamicRegionDensity || [1]).map((v) => Math.abs(v))
  );


  const dynamicRegionDensityCustomer = useMemo(() => {
    const map = {};
    filteredCustomers.forEach((m) => {
      if (!map[m.region]) map[m.region] = 0;
      map[m.region] += (m.revenue / MAX_CUT_OFF) * 100; 
          // sum growth if multiple markets per region
          // normalize near zero values so they can be seen lol
    });
    return map;
  }, [filteredCustomers]);
  
  const maxAbsDensityCustomer = Math.max(
    ...Object.values(dynamicRegionDensityCustomer || [1]).map((v) => Math.abs(v))
  );
  
  return (
    <>
      {/* Month selection & navigation */}
      <div className="mb-4 w-full">
        <div className="flex items-center justify-center">
          <button
            onClick={handlePrevMonth}
            className="px-4 py-2 bg-blue-500 cursor-pointer rounded hover:bg-blue-600 mr-3"
          >
            Prev Month
          </button>

          <button
            onClick={() => setSelectedMonth(new Date().toISOString().slice(0, 7))}
            className="px-4 py-2 bg-green-500 cursor-pointer rounded hover:bg-green-600 mr-3 text-white"
          >
            Now
          </button>

          <div className="flex flex-col">
            <label className="mr-2 font-bold text-blue-500">View Month:</label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border rounded px-3 py-1"
            />
            <span className="text-sm italic text-gray-500 mt-1">
              You can also click the date
            </span>
          </div>

          <button
            onClick={handleNextMonth}
            className="px-4 py-2 bg-blue-500 cursor-pointer rounded hover:bg-blue-600 ml-3"
          >
            Next Month
          </button>
        </div>
      </div>

      {/* Main grid layout */}
      <div className="p-6 w-full md:grid md:grid-cols-2 gap-6">

        {/* Mini KPI Cards with Sparklines */}
        <div className="grid grid-cols-2 grid-rows-2 gap-4 mb-4 md:flex md:flex-wrap md:justify-between md:items-center md:col-span-2">

          {/* Active Customers */}
          <Card className="flex flex-col items-center bg-blue-50 text-blue-800">
            <p className="text-sm font-medium">Active Customers</p>
            <p className="text-lg font-bold">{filteredCustomers.length}</p>
<div style={{ width: 100, height: 30 }}>
  <ResponsiveContainer width="100%" height="100%">
  </ResponsiveContainer>
</div>
          </Card>

          {/* Active Suppliers */}
          <Card className="flex flex-col items-center bg-green-50 text-green-800">
            <p className="text-sm font-medium">Active Suppliers</p>
            <p className="text-lg font-bold">{filteredSuppliers.length}</p>
<div style={{ width: 100, height: 30 }}>
  <ResponsiveContainer width="100%" height="100%">
  </ResponsiveContainer>
</div>          </Card>

          {/* Regions Covered Customers */}
          <Card className="flex flex-col items-center bg-purple-50 text-purple-800">
            <p className="text-sm font-medium">Customer Regions Covered</p>
            <p className="text-lg font-bold">{filteredCustomers.length}</p>
<div style={{ width: 100, height: 30 }}>
  <ResponsiveContainer width="100%" height="100%">
  </ResponsiveContainer>
</div>          </Card>
          
            {/* Regions Covered Suppliers */}
            <Card className="flex flex-col items-center bg-purple-50 text-purple-800">
              <p className="text-sm font-medium">Customer Regions Covered</p>
              <p className="text-lg font-bold">{filteredSuppliers.length}</p>
<div style={{ width: 100, height: 30 }}>
  <ResponsiveContainer width="100%" height="100%">
  </ResponsiveContainer>
</div>            </Card>

          {/* Top 5 Revenue % */}
          <Card className="flex flex-col items-center bg-purple-50 text-purple-800">
            <p className="text-sm font-medium">Top 5 Customer Revenue %</p>
            <p className="text-lg font-bold">
              {Math.round(
                (filteredCustomers.slice(0, 5).reduce((a, c) => a + c.revenue, 0) /
                  filteredCustomers.reduce((a, c) => a + c.revenue, 0)) *
                  100
              )}
              %
            </p>
<div style={{ width: 100, height: 30 }}>
  <ResponsiveContainer width="100%" height="100%">
  </ResponsiveContainer>
</div>          </Card>

        </div>

        {/* Global KPIs */}
        <div className="grid grid-cols-2 grid-rows-2 gap-4 mb-4 md:flex md:flex-wrap md:justify-between md:items-center md:col-span-2">
          <Card>
            <h2 className="text-sm font-medium text-gray-500">Total Revenue</h2>
            <p className="md:text-xl font-bold">
              {filteredCustomers.reduce((a, c) => a + c.revenue, 0).toLocaleString()} AED
            </p>
          </Card>

          <Card>
            <h2 className="text-sm font-medium text-gray-500">Top Customer Revenue %</h2>
            <p className="md:text-xl font-bold">
              {topCustomerPercent.toFixed(1)}% — {filteredCustomers[0]?.name ?? "N/A"}
            </p>
          </Card>

          <Card>
            <h2 className="text-sm font-medium text-gray-500">Top Supplier Cost %</h2>
            <p className="md:text-xl font-bold">{topSupplierPercent.toFixed(1)}% — {filteredSuppliers[0]?.name}</p>
          </Card>

        {/* Customer/Supplier Top Region */}
          <Card>
            <h2 className="text-sm font-medium text-gray-500">Top Regions</h2>
            <p className="md:text-xl font-bold">Supplier ({filteredSuppliers[0]?.region} — {filteredSuppliers[0]?.name})</p>
            <p className="md:text-xl font-bold">Customer ({filteredCustomers[0]?.region} — {filteredCustomers[0]?.name})</p>
          </Card>

        </div>

        {/* Supplier Pie Chart */}
        <Card className="p-0 bg-blue-50 flex-1 md:max-w-screen flex flex-col md:flex-none md:min-w-0">
          {/* Header with CSV export and filter */}
          <div className="flex flex-col gap-2 md:gap-0 justify-between mb-2 md:flex-row items-center">
            <button
              onClick={() => exportCSV(filteredSuppliers, "suppliers.csv")}
              className="mt-2 bg-green-400 hover:bg-green-500 px-3 py-1 rounded text-sm flex items-center gap-2 cursor-pointer text-green-900 font-semibold transform transition-transform duration-200 hover:-translate-y-1"
            >
            <img src={downloadLogo} alt="Download" className="w-6 h-6" />
              <span>Download CSV</span>
            </button>

            <h2 className="text-lg font-bold text-blue-800">Supplier Dependence {formatMonth(selectedMonth)}</h2>

            <select
              className="border px-2 py-1 rounded text-sm"
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
            >
              <option>All</option>
              {[...new Set(filteredSuppliers.map((s) => s.region))].map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Supplier PieChart */}
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={filteredSuppliers.slice(0, 5)}
                dataKey="share"
                nameKey="name"
                outerRadius={100}
                labelLine={true}
                label={true}
              >
                {filteredSuppliers.slice(0, 5).map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={["#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6"][index % 5]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value) => value.toLocaleString()} />

              {/* Custom Legend */}
              <Legend
                verticalAlign="bottom"
                height={80}
                content={() => (
                  <div className="flex justify-center gap-4 flex-wrap mt-2">
                    {filteredSuppliers.slice(0, 5).map((entry, index) => {
                      const color = ["#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6"][index % 5];
                      return (
                        <div key={index} className="flex items-center gap-1">
                          <span
                            style={{ backgroundColor: color }}
                            className="w-3 h-3 rounded block"
                          />
                          <span className="text-sm">{entry.name} ({entry.share}%)</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Supplier Table below pie chart */}
          <div className="overflow-x-auto max-h-40">
            <table className="min-w-full text-sm text-left mt-2">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-2 py-1">Supplier</th>
                  <th className="px-2 py-1">Share %</th>
                  <th className="px-2 py-1">Last Month</th>
                  <th className="px-2 py-1">Region</th>
                  <th className="px-2 py-1">Alert</th>
                </tr>
              </thead>
              <tbody>

                {filteredSuppliers.slice(0, 10).map((s, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-2 py-1">{s.name}</td>
                    <td className="px-2 py-1">{s.share}%</td>
                    <td className={`px-2 py-1 ${s.growth >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {s.growth >= 0 ? "▲" : "▼"} {Math.abs(s.growth)}%
                    </td>
                    <td className="px-2 py-1">{s.region}</td>
                    <td className="px-2 py-1">
                      {s.share > 50 && (
                        <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs">
                          High Risk
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Customer card right of piechart */}
        <Card>
          {/* Header with CSV export and filter */}
          <div className="flex flex-col gap-2 md:gap-0 justify-between mb-2 md:flex-row items-center">
            <button
              onClick={() => exportCSV(filteredCustomers, "customers.csv")}
              className="mt-2 bg-green-400 hover:bg-green-500 px-3 py-1 rounded text-sm flex items-center gap-2 cursor-pointer text-green-900 font-semibold transform transition-transform duration-200 hover:-translate-y-1"
            >
            <img src={downloadLogo} alt="Download" className="w-6 h-6" />
              <span>Download CSV</span>
            </button>

            <h2 className="text-lg font-bold text-blue-800">Top Customers {formatMonth(selectedMonth)}</h2>
            <select
              className="border px-2 py-1 rounded text-sm"
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <option>All</option>
              {[...new Set(customerData.map((c) => c.region))].map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Customer BarChart */}
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={filteredCustomers.slice(0, 5)}>
              <defs>
                <linearGradient id="revenueGreen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.9} />
                  <stop offset="95%" stopColor="#bbf7d0" stopOpacity={0.9} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" />
              <YAxis
                tickFormatter={(value) => {
                  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
                  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
                  return value;
                }}
              />
              <Tooltip formatter={(value) => value.toLocaleString()} />
              <Bar dataKey="revenue" fill="url(#revenueGreen)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          <p className="mt-2 text-sm text-gray-600 mb-2">
                {Math.round(
                  (filteredCustomers.slice(0, 5).reduce((a, c) => a + c.revenue, 0) /
                    filteredCustomers.reduce((a, c) => a + c.revenue, 0)) *
                    100
                )}% of revenue comes from top 5 customers.
          </p>

          {/* Customer Table */}
          <div className="overflow-x-auto max-h-40">
            <table className="min-w-full text-sm text-left">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-2 py-1">Customer</th>
                  <th className="px-2 py-1">Revenue</th>
                  <th className="px-2 py-1">Growth</th>
                  <th className="px-2 py-1">Region</th>
                  <th className="px-2 py-1">Alert</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.slice(0, 10).map((c, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="px-2 py-1">{c.name}</td>
                    <td className="px-2 py-1">{c.revenue.toLocaleString()} AED</td>
                    <td className={`px-2 py-1 ${c.growth >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {c.growth >= 0 ? "▲" : "▼"} {Math.abs(c.growth)}%
                    </td>
                    <td className="px-2 py-1">{c.region}</td>
                    <td className="px-2 py-1">
                      {c.revenue > MAX_CUT_OFF && (
                        <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                          Top Contributor
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Supplier Heatmap */}
        <Card className="col-span-2">
          <h2 className="text-lg font-bold mb-2">Supplier Heatmap (By Region and Cost)</h2>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Growth BarChart */}
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredSuppliers}>
                <XAxis dataKey="region" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="share">
                  {filteredSuppliers.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.growth > 0 ? "#22c55e" : "#ef4444"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Heatmap */}
            <div className="md:max-w-screen w-full md:w-1/2 flex flex-wrap justify-start gap-4">
              {Object.entries(dynamicRegionDensity).map(([region, value]) => {
                const intensity = Math.abs(value) / maxAbsDensity; // proportional intensity
                const color = value >= 0
                  ? `rgba(34,197,94,${intensity})`   // green
                  : `rgba(239,68,68,${intensity})`; // red
                return (
                  <div key={region} className="flex flex-col items-center mb-2">
                    <div
                      className="w-15 md:w-20 h-20 rounded mb-1"
                      style={{ backgroundColor: color }}
                      title={`${region}: ${value}% growth`}
                    />
                    <span className="text-sm">{region}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Customer & supplier density across GCC regions → visual insight.
          </p>
        </Card>

        {/* Customer Heatmap */}
        <Card className="col-span-2">
          <h2 className="text-lg font-bold mb-2">Customer Heatmap (By Region and Sales)</h2>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Growth BarChart */}
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={filteredCustomers}>
                <XAxis dataKey="region" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="revenue">
                  {filteredCustomers.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.revenue > 0 ? "#22c55e" : "#ef4444"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Heatmap */}
            <div className="md:max-w-screen w-full md:w-1/2 flex flex-wrap justify-start gap-4">
              {Object.entries(dynamicRegionDensityCustomer).map(([region, value]) => {
                const intensity = Math.abs(value) / maxAbsDensityCustomer; // proportional intensity
                const color = value >= 0
                  ? `rgba(34,197,94,${intensity})`   // green
                  : `rgba(239,68,68,${intensity})`; // red
                return (
                  <div key={region} className="flex flex-col items-center mb-2">
                    <div
                      className="w-15 md:w-20 h-20 rounded mb-1"
                      style={{ backgroundColor: color }}
                      title={`${region}: ${value}% growth`}
                    />
                    <span className="text-sm">{region}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Customer & supplier density across GCC regions → visual insight.
          </p>
        </Card>
        
      </div>
    </>
  );
};

export default CustomerSupplierMap;
